import {Component, Input, OnInit} from '@angular/core';
import {BsModalService} from "ngx-bootstrap/modal";
import {PermissionsService} from "../../services/permissions/permissions.service";
import {Router} from "@angular/router";
import {IdService} from "../../services/id/id.service";
import {SharedService} from "../../services/shared/shared.service";
import {AssetsService} from "../../services/assets/assets.service";
import Constants from "../../shared/constants";
import { TranslateService } from "@xref/ngx";
import { MFA_FEATURE_FLAG } from "../../services/two-factor/two-factor.service";

@Component({
    selector: 'xlog-user-verify',
    templateUrl: './user-verify.component.pug',
    styleUrls: ['./user-verify.component.scss'],
    standalone: false
})
export class UserVerifyComponent implements OnInit {
    @Input() email: string;
    firstName = '';
    lastName = '';
    uuid: string;
    hasValidNavigation: boolean;
    textVerifyValuePassword = '';
    inputType = 'password';
    token = '';
    model: any;
    errorText = '';
    loaded = true;
    preloadedImageEyeClosed: any;
    preloadedImageEyeOpen: any;
    preloadedImageDanger: any;

    constructor(
        private modalService: BsModalService,
        private featureFlagService: PermissionsService,
        private router: Router,
        private idService: IdService,
        private assetsService: AssetsService,
        private sharedService: SharedService,
        private translateService: TranslateService) {
        if (this.featureFlagService.isFeatureActivated(MFA_FEATURE_FLAG)) {
            this.restoreSignupUserDataWithMfaRules();
        } else {
            this.restoreSignupUserDataLegacy();
        }
    }

    ngOnInit() {
        this.preloadImage();

        // Component Library V2.0 does not support Wrap text in Tertiary Buttons,
        // so used this method to force Wrap text, text-align right and set specific width of button
        setTimeout(() => {
            const tertiaryButton = document.querySelector('.tertiary-button');
            tertiaryButton.classList.add('wrap-text');
        });
    }

    async preloadImage() {
        this.preloadedImageEyeClosed = await this.assetsService.getIconsSVG(Constants.ASSETS_URLS.EYE_CLOSED_ICON);
        this.preloadedImageEyeOpen = await this.assetsService.getIconsSVG(Constants.ASSETS_URLS.EYE_OPEN_ICON);
        this.preloadedImageDanger = await this.assetsService.getIconsSVG(Constants.ASSETS_URLS.ICON_DANGER);
    }

    redirectionTermsAndCondition() {
        window.open(Constants.SIGNUP_TERMS_XREF_URL, "_blank");
    }

    onSubmit() {
        if (!this.textVerifyValuePassword) {
            return;
        }
        this.loaded = false;
        this.idService.validatePassword(this.token, {password: this.textVerifyValuePassword}).subscribe(
            (response) => {
                this.loaded = true;
                if (this.featureFlagService.isFeatureActivated(MFA_FEATURE_FLAG)) {
                    this.persistSignupUserDataForMfa();
                } else {
                    this.persistSignupUserDataLegacy();
                }
                this.router.navigate(['/org-creation']);
            },
            (error) => {
                this.loaded = true;
                if (this.featureFlagService.isFeatureActivated(MFA_FEATURE_FLAG)) {
                    this.handleValidatePasswordErrorWithMfa(error);
                } else {
                    this.handleValidatePasswordErrorLegacy(error);
                }
            }
        );
    }

    toggleShowPassword() {
        this.inputType = this.inputType === 'text' ? 'password' : 'text';
    }

    redirectionToSignup() {
        window.location.href = '/signup';
    }

    redirectToForgottenPassword(): void {
        window.open('/forgotten-password', '_blank');
    }

    /** MFA on: try/catch + strict _expires restore (ENT-11812). */
    private restoreSignupUserDataWithMfaRules() {
        const stored = localStorage.getItem('signupUserData');
        let state = null;
        try {
            state = stored ? JSON.parse(stored) : null;
        } catch (e) {
            state = null;
        }

        const isExpired = !state
            || typeof state._expires !== 'number'
            || state._expires <= Date.now();

        if (isExpired) {
            localStorage.removeItem('signupUserData');
            this.hasValidNavigation = false;
            return;
        }

        this.applySignupState(state);
    }

    /** MFA off: legacy email/uuid restore (no try/catch, no _expires). */
    private restoreSignupUserDataLegacy() {
        const stored = localStorage.getItem('signupUserData');
        const state = stored ? JSON.parse(stored) : null;
        this.applySignupState(state);
    }

    private applySignupState(state: any) {
        if (state?.email && state?.uuid) {
            this.email = state.email;
            this.firstName = state.firstName || '';
            this.lastName = state.lastName || '';
            this.uuid = state.uuid;
            this.token = state.token;
            this.hasValidNavigation = true;
        }
    }

    /** MFA on: keep lockout restore snapshot, then continue to org creation. */
    private persistSignupUserDataForMfa() {
        const userDetails = this.buildUserDetails();
        localStorage.setItem('previousUrl', '/user-verify');
        localStorage.setItem('signupUserData', JSON.stringify({
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
            uuid: this.uuid,
            token: this.token,
            _expires: Date.now() + (30 * 60 * 1000),
        }));
        this.sharedService.setUserData(userDetails);
    }

    /** MFA off: clear signupUserData after successful verify (pre-MFA behaviour). */
    private persistSignupUserDataLegacy() {
        const userDetails = this.buildUserDetails();
        localStorage.setItem('previousUrl', '/user-verify');
        localStorage.removeItem('signupUserData');
        this.sharedService.setUserData(userDetails);
    }

    private buildUserDetails() {
        return {
            first_name: this.firstName,
            last_name: this.lastName,
            email: this.email,
            uuid: this.uuid,
            password: this.textVerifyValuePassword,
            token: this.token,
            previousUrl: '/user-verify'
        };
    }

    private handleValidatePasswordErrorWithMfa(error: any) {
        const xrefError = error?.error?.data?.xref_error;
        if (xrefError && (xrefError.key === 'XREF_MULTI_FACTOR_VERIFY'
            || xrefError.key === 'XREF_MULTI_FACTOR_SETUP')) {
            // Existing user with Enterprise MFA: keep the signup data, flag the
            // flow, and gate on MFA before returning to org creation.
            this.persistSignupUserDataForMfa();
            sessionStorage.setItem(Constants.SIGNUP_MFA_IN_PROGRESS, 'true');
            this.router.navigate(['two-factor', {t: xrefError.detail}]);
            return;
        }
        this.handleValidatePasswordErrorLegacy(error);
    }

    private handleValidatePasswordErrorLegacy(error: any) {
        if (error.status === 400) {
            this.errorText = this.translateService.instant('signUpText.verifyEmailPage.errors.invalidPassword');
        }
    }
}
